import { h, setChildren } from "../core/dom.js";
import { api } from "../api.js";
import { icon } from "../lib/icons.js";
import { openModal, toast, spinner } from "./ui.js";

// A Markdown export dialog shared by workouts, programs and exercises. `build({ perf })` returns
// the document text — `perf` is a Map<library_exercise_id, sessions[]> when the user opts into
// recent performance, otherwise null (plan-only). `exerciseIds` (optional) enables the toggle and
// is what we fetch history for; omit it to hide the toggle entirely.
export function openExportDoc({ title, build, exerciseIds = [] } = {}) {
  const ids = [...new Set(exerciseIds)].filter((id) => id != null && !String(id).startsWith("tmp-"));
  let includePerf = false;
  let perf = null; // Map once loaded
  let loading = false;

  const body = h("div");
  const m = openModal({ title: `Export "${title}"`, content: body });
  paint();

  function text() {
    return build({ perf: includePerf ? perf : null });
  }

  function download() {
    const slug = (title || "export").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "export";
    const blob = new Blob([text()], { type: "text/markdown;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = h("a", { href: url, download: `${slug}.md` });
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  function copy() {
    navigator.clipboard?.writeText(text()).then(
      () => toast("Markdown copied", "success"),
      () => toast("Copy failed", "error")
    );
  }

  async function onToggle(e) {
    includePerf = e.target.checked;
    if (includePerf && !perf && ids.length) {
      loading = true;
      paint();
      try {
        const results = await Promise.all(ids.map((id) => api.exerciseHistory(id).catch(() => [])));
        perf = new Map(ids.map((id, i) => [id, results[i]]));
      } catch {
        toast("Couldn't load performance", "error");
        includePerf = false;
      }
      loading = false;
    }
    paint();
  }

  function paint() {
    setChildren(body,
      h("p", { class: "dim", style: { marginTop: 0 } },
        "A readable document you can hand to a coach or paste into an LLM for feedback."),
      ids.length
        ? h("label", { class: "row-between", style: { padding: "6px 0", cursor: "pointer" } },
            h("span", null, "Include recent performance"),
            h("input", { type: "checkbox", checked: includePerf, disabled: loading, onchange: onToggle }))
        : null,
      loading
        ? spinner("Loading performance…")
        : h("textarea", {
            class: "input", rows: 12, readonly: true,
            style: { resize: "vertical", fontFamily: "ui-monospace, monospace", fontSize: ".8rem", whiteSpace: "pre", overflowWrap: "normal" },
            onclick: (e) => e.target.select(),
          }, text()),
      h("div", { class: "btn-row mt" },
        h("button", { class: "btn btn-primary grow", onClick: copy }, icon("copy", { size: 18 }), "Copy"),
        h("button", { class: "btn btn-ghost grow", onClick: download }, icon("download", { size: 18 }), "Download .md"))
    );
  }

  return m;
}
